import cx from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import Slider from "components/DesignSystem/Slider/Slider";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { PaperPlaneRight } from "components/icons/Chat/PaperPlaneRight";
import { Cross } from "components/icons/Cross";
import { Info } from "components/icons/Info";
import { ThreadMessageBubble } from "components/MessageBubble/MessageBubble";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import Lottie from "lottie-react";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import {
  Channel as StreamChannel,
  MessageInput as StreamInput,
} from "stream-chat-react";
import {
  DefaultStreamChatGenerics,
  StreamMessage,
  useChannelStateContext,
  useChatContext,
} from "stream-chat-react";
import DumDumGeneral from "lottie/DumDumGeneral.json";
import GeneralToThink from "lottie/GeneralTothink.json";
import ThinkToIdeaToGeneral from "lottie/ThinkToIdeaToGeneral.json";
import ThinkingToSad from "lottie/ThinkingToSad.json";
import { useAppSelector } from "hooks/useAppSelector";
import {
  ANIMATION_STATES,
  resetToGeneral,
  showSad,
} from "store/slices/TaxAIResponse";
import { useDispatch } from "react-redux";
import { showSuccess } from "store/slices/TaxAIResponse";
import { Event, MessageResponse } from "stream-chat";
import { aiErrorMessage, StableThreadMessage } from "constants/threadChat";
import { Banner } from "components/DesignSystem/Banner/Banner";
import classNames from "classnames";
import { DateSeparator } from "components/chat/DateSeparator";
import { TypingIndicator } from "components/chat/TypingIndicator";
import { SystemMessage } from "components/chat/SystemMessage";
import { EmptyStateIndicator } from "components/chat/EmptyStateIndicator";
import { MessageInput } from "components/MessageInput/MessageInput";
import { AI_CHANNEL } from "constants/chatType";
import { useModal } from "hooks/useModal";
import { CustomData } from "types/Models/reviewDocumentAndBalancingPayment";
import { ThreadManualAnalysisTrigger } from "./ThreadManualAnalysisTrigger";

type ThreadSliderProps = {
  open: boolean;
  onClose: () => void;
  threadMessage:
    | (MessageResponse<DefaultStreamChatGenerics> & {
        custom_data?: CustomData;
      })
    | null;
  isSummaryThread?: boolean;
  headerDescription: string;
  EmptyStateComponent?: React.ComponentType<any>;
  hideHeader?: boolean;
  askForAdminReview?: boolean;
};

const ThreadChatComponent = ({
  open,
  onClose,
  threadMessage,
  isSummaryThread = false,
  headerDescription,
  EmptyStateComponent,
  hideHeader,
  askForAdminReview,
}: ThreadSliderProps) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { client } = useChatContext();
  const { channel } = useChannelStateContext();
  const [threadInstance, setThreadInstance] =
    useState<MessageResponse<DefaultStreamChatGenerics> | null>(null);
  const [threadMessages, setThreadMessages] = useState<StreamMessage[]>([]);
  const [lastThreadMessage, setLastThreadMessage] =
    useState<StreamMessage<DefaultStreamChatGenerics> | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const initializationRef = useRef(false);
  const { alertToast } = useToast();
  const [showBanner, setShowBanner] = useState(false);
  const { showAIChannels, showOnlyAdminMessage } = useAppSelector(
    (state) => state.chatSearch
  );
  const aiMessageViewOnly = showAIChannels && !showOnlyAdminMessage;

  // Input state - simplified
  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { isCustomer, isAdmin } = useRoleBasedView();
  const animationState = useAppSelector(
    (state) => state.TaxAIResponse.animationState
  );
  const dispatch = useDispatch();
  const threadManualAnalysisModal = useModal();
  const aiAgentValidation = threadMessage?.custom_data?.ai_agent_validation;

  const ANIMATION_MAP: Record<string, any> = {
    [ANIMATION_STATES.THINKING]: GeneralToThink,
    [ANIMATION_STATES.SUCCESS]: ThinkToIdeaToGeneral,
    [ANIMATION_STATES.SAD]: ThinkingToSad,
  };

  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight;
    }
  }, []);

  const isAIChannel = channel?.data?.custom_type === AI_CHANNEL;

  // Auto-scroll when new messages are added
  useEffect(() => {
    if (threadMessages.length > 0) {
      setTimeout(scrollToBottom, 100);
    }
  }, [threadMessages.length, scrollToBottom]);

  // Initialize thread when slider opens
  useEffect(() => {
    if (open && threadMessage?.id && !initializationRef.current) {
      initializationRef.current = true;

      const initThread = async () => {
        try {
          // Use the channel's getReplies method instead
          const response = await channel.getReplies(threadMessage.id, {
            limit: 300,
          });

          if (response?.messages?.length === 1) {
            setShowBanner(response.messages[0]?.user?.id !== client.userID);
          } else {
            setShowBanner(false);
          }

          setThreadMessages(response.messages || []);
          setThreadInstance(threadMessage);
          setIsInitialized(true);

          if (response.messages && response.messages.length > 0) {
            const lastMessage = response.messages[response.messages.length - 1];
            setLastThreadMessage(lastMessage);
          }

          // Focus after initialization and scroll to bottom
          setTimeout(() => {
            scrollToBottom();
            if (sliderRef.current) {
              sliderRef.current.focus();
            }
          }, 100);
        } catch (error) {
          try {
            const channelState = await channel.query({
              messages: { limit: 300 },
              members: { limit: 100, offset: 0 },
            });

            // Filter messages that are replies to this thread
            const replies = channelState.messages.filter(
              (msg: any) => msg.parent_id === threadMessage.id
            );

            setThreadMessages(replies);
            setThreadInstance(threadMessage);
            setIsInitialized(true);

            if (replies.length > 0) {
              const lastMessage = replies[replies.length - 1];
              setLastThreadMessage(lastMessage);
            }

            // Scroll to bottom after fallback
            setTimeout(scrollToBottom, 100);
          } catch (fallbackError) {
            // Still show the UI even if we can't load replies
            setThreadInstance(threadMessage);
            setIsInitialized(true);
          }
        }
      };

      initThread();
    }

    if (!open) {
      // Reset everything when closing
      setIsInitialized(false);
      setThreadInstance(null);
      setThreadMessages([]);
      setLastThreadMessage(null);
      initializationRef.current = false;
    }
  }, [open, threadMessage?.id, channel, scrollToBottom]);

  // Listen for new thread messages
  useEffect(() => {
    if (!threadInstance || !client) return;

    const handleNewMessage = (event: any) => {
      if (event.message?.parent_id === threadInstance.id) {
        const isAIResponse = event.message?.user?.id !== client.userID;

        if (isAIResponse) {
          if (event.message.text === aiErrorMessage) {
            dispatch(showSad());
          } else {
            dispatch(showSuccess());
          }

          setTimeout(() => {
            dispatch(resetToGeneral());
          }, 5000);
        }

        setThreadMessages((prev) => {
          const exists = prev.find((m) => m.id === event.message.id);
          if (exists) return prev;

          if (prev.length === 0) {
            setShowBanner(event.message?.user?.id !== client.userID);
          } else {
            setShowBanner(false);
          }

          const newMessages = [...prev, event.message];
          const lastMessage = newMessages[newMessages.length - 1];
          setLastThreadMessage(lastMessage);
          return newMessages;
        });
      }
    };

    const handleMessageUpdated = (event: Event<DefaultStreamChatGenerics>) => {
      if (!event.message) return;

      setThreadMessages((prev) =>
        prev.map((msg) => (msg.id === event.message!.id ? event.message! : msg))
      );

      if (event.message.id === threadInstance?.id) {
        setThreadInstance(event.message);
      }
    };

    client.on("message.new", handleNewMessage);
    client.on("message.updated", handleMessageUpdated);

    return () => {
      client.off("message.new", handleNewMessage);
      client.off("message.updated", handleMessageUpdated);
    };
  }, [threadInstance?.id, client, dispatch]);

  const handleClose = useCallback(() => {
    setIsInitialized(false);
    setThreadInstance(null);
    setThreadMessages([]);
    setLastThreadMessage(null);
    initializationRef.current = false;
    onClose();
  }, [onClose]);

  // Function to send a new reply
  const sendReply = useCallback(async () => {
    if (!threadInstance || !inputValue.trim() || isSending) return;

    setIsSending(true);
    try {
      const newMessage = await channel.sendMessage({
        text: inputValue.trim(),
        parent_id: threadInstance.id,
      });

      // Clear input after successful send
      setInputValue("");

      // The message should be automatically added via the event listener
      if (newMessage.message) {
        setThreadMessages((prev) => {
          const exists = prev.find((m) => m.id === newMessage.message!.id);
          if (exists) return prev;

          const newMessages = [...prev, newMessage.message!];
          setLastThreadMessage(newMessage.message!);
          return newMessages;
        });
      }

      // Refocus the input after sending
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    } catch (error) {
      alertToast({ message: "Error sending reply. Please try again." });
    } finally {
      setIsSending(false);
    }
  }, [threadInstance, channel, inputValue, isSending]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendReply();
      }
    },
    [sendReply]
  );

  // Handle change
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInputValue(e.target.value);
    },
    []
  );

  // Auto-focus input when slider opens
  useEffect(() => {
    if (open && isInitialized && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open, isInitialized]);

  // If no threadMessage provided and EmptyStateComponent exists for Inkle AI chat
  if (!threadMessage && EmptyStateComponent) {
    return <EmptyStateComponent />;
  }

  // Don't render anything if not initialized
  if (!open || !isInitialized || !threadInstance) {
    return (
      <div
        className="t-h-full t-flex t-flex-col t-bg-neutral-0 t-flex-1"
        tabIndex={-1}
      >
        {!hideHeader && (
          <div className="t-p-5 t-flex t-justify-between t-bg-white t-w-full t-border t-border-solid t-border-t-0 t-border-l-0 t-border-r-0 t-border-neutral-10 t-items-center">
            <div className="t-flex t-items-center t-gap-3">
              <Lottie
                className="t-w-[42px]"
                animationData={ANIMATION_MAP[animationState] || DumDumGeneral}
              />
              <div>
                <div className="t-flex t-gap-1.5 t-items-center">
                  <div className="t-text-subtext t-text-text-100">AI Agent</div>
                  <Tag tagType="purple" rounded icon={false} size="small">
                    Beta
                  </Tag>
                </div>
                <div className="t-text-body-sm t-text-text-30">
                  {animationState === ANIMATION_STATES.THINKING
                    ? "AI is thinking..."
                    : animationState === ANIMATION_STATES.SAD
                    ? "Something went wrong"
                    : headerDescription}
                </div>
              </div>
            </div>
            {!isSummaryThread && (
              <Button customType="ghost" size="small" onClick={handleClose}>
                <Cross />
              </Button>
            )}
          </div>
        )}
        <div className="t-flex-1 t-overflow-y-auto t-p-4 threadBody">
          <div className="t-flex t-items-center t-justify-center t-h-full">
            <div className="t-text-text-30">Loading thread...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="t-h-full t-flex t-flex-col t-bg-neutral-0 t-w-full"
      tabIndex={-1}
    >
      {/* Header */}
      {!hideHeader && (
        <div className="t-p-5 t-flex t-justify-between t-bg-white t-w-full t-border t-border-solid t-border-t-0 t-border-l-0 t-border-r-0 t-border-neutral-10 t-items-center">
          <div className="t-flex t-items-center t-gap-3">
            <Lottie
              className="t-w-[42px]"
              animationData={ANIMATION_MAP[animationState] || DumDumGeneral}
            />
            <div>
              <div className="t-flex t-gap-1.5 t-items-center">
                <div className="t-text-subtext t-text-text-100">AI Agent</div>
                <Tag tagType="purple" rounded icon={false} size="small">
                  Beta
                </Tag>
              </div>
              <div className="t-text-body-sm t-text-text-30">
                {animationState === ANIMATION_STATES.THINKING
                  ? "AI is thinking..."
                  : animationState === ANIMATION_STATES.SAD
                  ? "Something went wrong"
                  : headerDescription}
              </div>
            </div>
          </div>
          <Button customType="ghost" size="small" onClick={handleClose}>
            <Cross />
          </Button>
        </div>
      )}

      <div
        className="t-flex-1 t-overflow-y-auto t-p-4 threadBody"
        ref={scrollContainerRef}
      >
        <div className="t-space-y-4">
          {!isSummaryThread && (
            <ThreadMessageBubble
              message={threadInstance as StreamMessage}
              isThreadMessage
              lastThreadMessage={
                threadMessages.length > 0
                  ? threadMessages[threadMessages.length - 1]
                  : threadInstance ?? undefined
              }
            />
          )}

          {threadMessages.map((msg, index) => {
            const previousMessageDate =
              index === 0
                ? threadInstance?.created_at
                : threadMessages[index - 1]?.created_at;

            const isNewDay =
              msg.created_at &&
              previousMessageDate &&
              new Date(msg.created_at).toDateString() !==
                new Date(previousMessageDate).toDateString();

            return (
              <div key={msg.id}>
                {isNewDay && isAIChannel && msg.created_at && (
                  <DateSeparator date={new Date(msg.created_at)} />
                )}

                <ThreadMessageBubble
                  message={msg}
                  lastThreadMessage={threadMessages[threadMessages.length - 1]}
                  isThreadMessage
                  isSummaryThread={isSummaryThread}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div>
        {askForAdminReview && isAdmin ? (
          <>
            <ThreadManualAnalysisTrigger
              channelId={channel.id}
              messageId={threadMessage?.id}
              aiAgentValidation={aiAgentValidation}
            />
          </>
        ) : (
          <>
            {showBanner && !isAIChannel && (
              <div className="t-p-4">
                <Banner
                  type="info"
                  description={
                    <div className="t-flex t-gap-2 t-items-start">
                      <div className="t-text-body-sm t-text-text-30">
                        Didn’t answer your query? Don’t worry — our support
                        agent will follow up as soon as they’re available
                      </div>
                      <Button
                        customType="ghost"
                        size="xsmall"
                        onClick={() => setShowBanner(false)}
                      >
                        <Cross />
                      </Button>
                    </div>
                  }
                />
              </div>
            )}
            {!aiMessageViewOnly && (
              <div
                className={classNames(
                  "t-text-neutral-40 t-flex t-gap-1 t-items-center t-text-body-sm t-p-4",
                  {
                    "t-py-2": showBanner,
                    "t-pb-2": !showBanner,
                  }
                )}
              >
                <Info stroke="1.2" size="14" />
                <div>
                  Our AI agent is still in Beta version, it can make mistakes.
                </div>
              </div>
            )}
          </>
        )}
        {!aiMessageViewOnly && (
          <>
            {isAIChannel ? (
              <div>
                <StreamChannel
                  Message={(props) => null}
                  Input={(props) => (
                    <MessageInput
                      {...props}
                      parent={threadInstance}
                      hideSLABanner
                    />
                  )}
                  channel={channel}
                  DateSeparator={DateSeparator}
                  TypingIndicator={TypingIndicator}
                  MessageSystem={SystemMessage}
                  EmptyStateIndicator={EmptyStateIndicator}
                  ThreadHeader={() => null}
                  ThreadStart={() => null}
                >
                  <StreamInput parent={threadInstance} />
                </StreamChannel>
              </div>
            ) : (
              <div className="t-p-4">
                <div
                  className="t-relative t-cursor-text t-rounded t-border t-border-solid t-border-neutral-10 t-bg-white t-p-3"
                  onClick={() => inputRef.current?.focus()}
                >
                  <div className="t-flex t-flex-col t-relative t-gap-3">
                    <div className="t-flex t-flex-col">
                      <textarea
                        ref={inputRef}
                        value={inputValue}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        placeholder="Reply to thread..."
                        className="t-w-full t-resize-none t-border-0 t-outline-none t-min-h-[18px] t-max-h-[200px]"
                        style={{ fontSize: "14px" }}
                        rows={2}
                        disabled={isSending || !isCustomer}
                      />

                      {isCustomer && (
                        <div className="t-flex t-w-full t-items-end t-justify-end">
                          <div>
                            <Button
                              onClick={sendReply}
                              customType="ghost_icon"
                              disabled={!inputValue.trim() || isSending}
                              size="small"
                              isLoading={isSending || !isCustomer}
                            >
                              <span
                                className={cx("t-transition-all", {
                                  "t-text-purple": inputValue.trim(),
                                  "t-text-neutral-40": !inputValue.trim(),
                                })}
                              >
                                <PaperPlaneRight />
                              </span>
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Export with simple comparison
export const ThreadChat = memo(ThreadChatComponent, (prevProps, nextProps) => {
  return (
    prevProps.open === nextProps.open &&
    prevProps.threadMessage?.id === nextProps.threadMessage?.id &&
    prevProps.threadMessage?.custom_data?.ai_agent_validation
      ?.thread_feedback_rating ===
      nextProps.threadMessage?.custom_data?.ai_agent_validation
        ?.thread_feedback_rating
  );
});
